/**
 * Los videos de la ficha, administrados desde el detalle del producto.
 *
 * Puede haber varios: cada uno que se sube va al final. A diferencia de las
 * fotos, el servidor no los convierte, así que tienen que llegar listos para la
 * web (MP4 H.264, sin audio, unos pocos MB). En la ficha van en su propia fila
 * de miniaturas, debajo de las de las fotos, con la etiqueta "Video referencial".
 *
 * Quitar uno pasa por un modal por lo mismo que quitar una foto: el archivo se
 * borra del almacén y no hay vuelta atrás.
 */
import { useState } from 'react';

import type { VideoProducto } from '@bandu/compartido';

import { useSubidaImagen } from '../../hooks/use-subida-imagen';
import { eliminarVideoProducto, subirVideoProducto } from '../../servicios/administracion.servicio';
import { Icono } from '../comunes/Icono';
import { Modal } from './Modal';
import { PanelAdmin } from './PanelAdmin';
import { SubidorImagen } from './SubidorImagen';

export interface VideoProductoAdminProps {
  productoId: number;
  videos: VideoProducto[];
  onCambio(videos: VideoProducto[]): void;
}

export function VideoProductoAdmin({ productoId, videos, onCambio }: VideoProductoAdminProps) {
  const subida = useSubidaImagen();
  const [porQuitar, setPorQuitar] = useState<VideoProducto | null>(null);

  function subir(archivo: File): void {
    void subida.subir(async () => {
      const nuevo = await subirVideoProducto(productoId, archivo);
      onCambio([...videos, nuevo]);
    });
  }

  function quitar(): void {
    if (porQuitar === null) return;
    const id = porQuitar.id;

    void subida.subir(async () => {
      await eliminarVideoProducto(productoId, id);
      onCambio(videos.filter((video) => video.id !== id));
      setPorQuitar(null);
    });
  }

  return (
    <PanelAdmin
      titulo="Videos"
      bajada="Opcionales. En la ficha van en su propia fila, debajo de las fotos, mudos y en loop, con la etiqueta «Video referencial»."
    >
      {subida.falla !== null ? (
        <p className="acceso-error" role="alert">
          <Icono nombre="alerta" />
          <span>{subida.falla}</span>
        </p>
      ) : null}

      {videos.length > 0 ? (
        <ul className="videos-admin">
          {videos.map((video, indice) => (
            <li key={video.id}>
              <video
                src={video.url}
                width={video.ancho}
                height={video.alto}
                muted
                loop
                playsInline
                controls
                preload="metadata"
              />
              <div className="video-admin-pie">
                <p className="galeria-admin-medida">
                  {video.ancho} × {video.alto}
                </p>
                <button
                  type="button"
                  className="btn btn-sm btn-peligro"
                  disabled={subida.subiendo}
                  aria-label={`Quitar el ${String(indice + 1)}° video`}
                  onClick={() => {
                    setPorQuitar(video);
                  }}
                >
                  Quitar
                </button>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="panel-vacio">Este producto no tiene videos.</p>
      )}

      <SubidorImagen
        etiqueta="elige un archivo"
        ayuda="MP4 (H.264) sin audio, de hasta 20 MB. Se publica tal cual: conviene que pese menos de 5 MB."
        acepta="video/mp4"
        que="un video"
        subiendo={subida.subiendo}
        onArchivo={subir}
      />

      <Modal
        titulo="Quitar este video"
        bajada="No se puede deshacer"
        abierto={porQuitar !== null}
        onCerrar={() => {
          if (!subida.subiendo) setPorQuitar(null);
        }}
      >
        <p className="parrafo-panel">
          El video se borra de la ficha y del almacen. Para volver a tenerlo hay que subir el
          archivo de nuevo.
        </p>
        <div className="modal-acciones">
          <button
            type="button"
            className="btn btn-contorno"
            disabled={subida.subiendo}
            onClick={() => {
              setPorQuitar(null);
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-peligro"
            disabled={subida.subiendo}
            onClick={quitar}
          >
            {subida.subiendo ? 'Quitando…' : 'Quitar el video'}
          </button>
        </div>
      </Modal>
    </PanelAdmin>
  );
}
